import { Autocomplete, Checkbox, FormControl, TextField } from '@mui/material';
import React, { useMemo } from 'react';
import coalesceClassNames from 'utils/coalesceClassNames';
import { isEmpty } from 'utils/helpers';
import FieldValidator from '../FieldValidator/FieldValidator';
import './MyAutocompleteInput.scss';

export type MyAutocompleteInputOption = {
    label: string;
    value: string;
};

export type MyAutocompleteInputProps = {
    id?: string;
    className?: string;
    label?: string;
    value?: string;
    onChangeMultiple?: (val: MyAutocompleteInputOption[]) => void;
    onChange?: (val: string) => void;
    validationKey?: string;
    validationRequired?: boolean | string;
    validationCustom?: false | string;
    options?: MyAutocompleteInputOption[];
    disabled?: boolean;
    multiple?: boolean;
    size?: 'small' | 'medium';
};

export default function MyAutocompleteInput({
    id,
    className,
    label,
    value,
    onChange,
    onChangeMultiple,
    validationKey,
    validationRequired,
    validationCustom,
    options = [],
    disabled = false,
    multiple = false,
    size = 'small',
}: MyAutocompleteInputProps) {
    const handleChange = (
        val?: MyAutocompleteInputOption | (MyAutocompleteInputOption | undefined)[] | null,
    ) => {
        if (multiple) {
            let selectedOpts;
            if (Array.isArray(val)) {
                selectedOpts = val.filter(Boolean) as MyAutocompleteInputOption[];
            } else {
                selectedOpts = [val].filter(Boolean) as MyAutocompleteInputOption[];
            }
            onChangeMultiple?.(selectedOpts);
        } else {
            const o: MyAutocompleteInputOption | null | undefined = Array.isArray(val)
                ? val[0]
                : val;
            onChange?.(o?.value ?? '');
        }
    };

    const selectedOption = useMemo(
        () => (isEmpty(value) ? null : options.find(o => o.value === value) ?? null),
        [options, value],
    );

    const selectedOptArray = useMemo(() => {
        const arr = Array.isArray(value) ? value : isEmpty(value) ? [] : [value];
        return arr.map(val => options.find(o => o.value === `${val}`)).filter(Boolean);
    }, [options, value]);

    return (
        <FieldValidator
            validationKey={validationKey}
            validationRequired={validationRequired}
            validationCustom={validationCustom}
            value={value}
        >
            {({ revealError, error }) => (
                <FormControl
                    className={coalesceClassNames(
                        'MyAutocompleteInput',
                        className,
                        error && 'MyAutocompleteInput--error',
                        multiple && 'MyAutocompleteInput--multiple',
                        label ? 'MyAutocompleteInput--with-label' : 'MyAutocompleteInput--no-label',
                    )}
                    size={size}
                >
                    <Autocomplete
                        id={id}
                        className={coalesceClassNames(
                            'MyAutocompleteInput__Autocomplete',
                            className && `${className}__Autocomplete`,
                        )}
                        options={options}
                        disabled={disabled}
                        size={size}
                        value={multiple ? selectedOptArray : selectedOption}
                        multiple={multiple}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={label}
                                className="MyAutocompleteInput__TextField"
                            />
                        )}
                        disableCloseOnSelect={multiple}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                {multiple && (
                                    <Checkbox
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                )}
                                {option?.label}
                            </li>
                        )}
                        onChange={(e, opts) => handleChange(opts)}
                        onBlur={revealError}
                        onClose={revealError}
                    />
                </FormControl>
            )}
        </FieldValidator>
    );
}
